import React, { useState, useEffect, useCallback, useRef, useMemo, lazy, Suspense } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import { useAuth } from '../../contexts/AuthContext';
import { StoryService } from '../../services/storyService';
import { useLocation, useNavigate, Location } from 'react-router-dom';
import { Timestamp, deleteField } from 'firebase/firestore';
import { useProgress } from '../../contexts/ProgressContext';
import SignupPrompt from '../Account/SignupPrompt';
import Notification from '../Account/Notification';
import { doc, updateDoc, getDoc, setDoc, query, where, orderBy, limit, collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/firebase';
import ChallengeSuccessModal from './ChallengeSuccessModal';
import ElegantChallengeBanner from './ElegantChallengeBanner';
import { useGuestMode } from '../../hooks/useGuestMode';
import { RegistrationGate } from '../auth/RegistrationGate';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../firebase/firebase';
import StoryPreview from './StoryPreview';
import { useSafetyCheck, SafetyIssue } from '../../hooks/useSafetyCheck'; // adjust path
import SafetyInlineWarning from '../Safety/SafetyInlineWarning';           // adjust path
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
 
// ─── Design tokens ────────────────────────────────────────────────────────────
const C = {
  primary:          '#6a37d4',
  primaryLight:     '#ae8dff',
  primaryContainer: '#ede8ff',
  secondary:        '#006947',
  secondaryContainer: '#69f6b8',
  secondaryDim:     '#58e7ab',
  tertiary:         '#6f5900',
  tertiaryContainer:'#fed01b',
  surface:          '#f3f6ff',
  surfaceContainer: '#dce9fd',
  surfaceHigh:      '#d6e3f9',
  surfaceHighest:   '#cfdef5',
  surfaceLow:       '#eaf1ff',
  surfaceLowest:    '#ffffff',
  onBackground:     '#252f3c',
  onSurface:        '#252f3c',
  onSurfaceVariant: '#525c6a',
  outlineVariant:   '#a4aebe',
  error:            '#b41340',
};
 
const gradientPrimary = `linear-gradient(135deg, ${C.primary}, ${C.primaryLight})`;
 
// ─── Type definitions ─────────────────────────────────────────────────────────
type ChapterBlock = {
  id: string;
  type: 'text' | 'image';
  content?: string;
  imageUrl?: string;
  caption?: string;
};
 
type Chapter = {
  id: number;
  title: string;
  content?: string;
  blocks?: ChapterBlock[];
};
 
const migrateChapterToBlocks = (chapter: any): Chapter => {
  if (chapter.blocks) return chapter;
  return {
    ...chapter,
    blocks: [{ id: `block-${Date.now()}`, type: 'text' as const, content: chapter.content || '' }],
  };
};
 
// Lazy loaded components
const EnhancedStoryPlanner = lazy(() => import('./EnhancedStoryPlanner'));
const ChapterMaster         = lazy(() => import('./ChapterMaster'));
const CoverDesigner         = lazy(() => import('./CoverDesigner'));
const CharacterSheet        = lazy(() => import('./CharacterSheet'));
 
interface LocationState {
  storyId?: string;
  isChallenge?: boolean;
  challengeType?: string;
  challengePrompt?: string;
  challengeTitle?: string;
  wordLimit?: number;
  challengeXP?: number;
  classId?: string;
}
 
// ─── Shared spinner ───────────────────────────────────────────────────────────
const FullScreenLoader = ({ label }: { label: string }) => (
  <div style={{
    display: 'flex', flexDirection: 'column', justifyContent: 'center',
    alignItems: 'center', minHeight: '100vh', gap: 16,
    background: C.surface, fontFamily: "'Epilogue', sans-serif",
  }}>
    <div style={{
      width: 48, height: 48, borderRadius: '50%',
      border: `4px solid ${C.primaryContainer}`,
      borderTopColor: C.primary,
      animation: 'spin 0.8s linear infinite',
    }} />
    <span style={{ color: C.onSurfaceVariant, fontWeight: 600 }}>{label}</span>
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);
 
// ─── Main Component ───────────────────────────────────────────────────────────
const RegularBookEditor: React.FC = () => {
 
  // ── Suppress ResizeObserver noise ─────────────────────────────────────────
  useEffect(() => {
    const handler = (e: ErrorEvent) => {
      if (e.message.includes('ResizeObserver') || e.message.includes('Node cannot be found')) {
        const overlay = document.getElementById('webpack-dev-server-client-overlay');
        if (overlay) overlay.style.display = 'none';
        e.stopImmediatePropagation(); e.preventDefault();
      }
    };
    const rejHandler = (e: PromiseRejectionEvent) => {
      if (e.reason?.message?.includes('ResizeObserver')) e.preventDefault();
    };
    window.addEventListener('error', handler);
    window.addEventListener('unhandledrejection', rejHandler);
    return () => { window.removeEventListener('error', handler); window.removeEventListener('unhandledrejection', rejHandler); };
  }, []);
 
  // ── Core hooks ────────────────────────────────────────────────────────────
  const { currentUser }            = useAuth();
  const { isGuestMode, saveDraft, updateDraft } = useGuestMode();
  const [showSignupPrompt, setShowSignupPrompt]   = useState(false);
  const [promptReason, setPromptReason]           = useState<'save' | 'share'>('save');
  const location  = useLocation() as Location<LocationState>;
  const navigate  = useNavigate();
  const renderCountRef   = useRef(0);
  const lastRenderTime   = useRef(Date.now());
  const [showLimitGate, setShowLimitGate]         = useState(false);
  const [isInitialLoad, setIsInitialLoad]         = useState(true);
  const hasInitializedWordCount                   = useRef(false);
  const [userClasses, setUserClasses]             = useState<any[]>([]);
  const [selectedClassId, setSelectedClassId]     = useState<string | null>(null);
  const [showPreview, setShowPreview]             = useState(false);
  const [characters, setCharacters]               = useState<any[]>([]);
  const activeBlockEditorRef                      = useRef<any>(null);
  const [, forceUpdate]                           = useState(0);
  const [chaptersOpen, setChaptersOpen]           = useState(true); // sidebar section toggle
  const autoSaveTimerRef                          = useRef<NodeJS.Timeout | null>(null);
  const hasLoadedRef                              = useRef(false); // prevents autosave on mount
  const [distractionFree, setDistractionFree]     = useState(false);
  const { runSaveCheck } = useSafetyCheck();
  const [inlineSafetyIssues, setInlineSafetyIssues] = useState<SafetyIssue[]>([]);
  const [authorName, setAuthorName] = useState<string>('');
 
  const [challengeData] = useState(() => {
    const state = location.state as any;
    if (state?.isChallenge) return { isChallenge: true, type: state.challengeType, xp: state.challengeXP, goal: state.challengeGoal, title: state.challengeTitle };
    return null;
  });
 
  const [challengeCompleted, setChallengeCompleted] = useState(false);
  const [showSuccessModal, setShowSuccessModal]     = useState(false);
  const chapterSwitchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
 
  const { xp, level, streak, achievements, addXP, addWords, incrementChaptersThisWeek, updateStreak, completeChallenge, isChallengeCompleted } = useProgress();
 
  const [showFeedbackPanel, setShowFeedbackPanel] = useState(false);
  const [teacherFeedback, setTeacherFeedback]     = useState<any>(null);
  const [loadingFeedback, setLoadingFeedback]     = useState(false);
 
  const searchParams  = new URLSearchParams(location.search);
  const urlStoryId    = searchParams.get('storyId');
  const [storyId, setStoryId] = useState<string | null>(urlStoryId || location.state?.storyId || null);
  const storyIdRef    = useRef<string | null>(urlStoryId || location.state?.storyId || null); // stays current for async upload callbacks
  const [loading, setLoading] = useState(!!storyId);
 
  const locationState  = location.state as any;
  const { assignmentId, assignmentTitle, prompt: assignmentPrompt, classId: stateClassId } = locationState || {};
 
  const [saving, setSaving]                   = useState(false);
  const [lastSaved, setLastSaved]             = useState<Date | null>(null);
  const [activeTool, setActiveTool]           = useState<string | null>(null);
  const [currentChapter, setCurrentChapter]   = useState(0);
  const [storyTitle, setStoryTitle]           = useState('My Awesome Story');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isMobile, setIsMobile]               = useState(window.innerWidth < 768);
  const [showChapterDropdown, setShowChapterDropdown] = useState(false);
  const [mobileView, setMobileView]           = useState<'home' | 'write' | 'tools' | 'settings'>('write');
  const [coverData, setCoverData]             = useState<any>(null);
  const [coverDataLoading, setCoverDataLoading] = useState(false);
  const [planData, setPlanData]               = useState<any>(null);
  const [showToolsMenu, setShowToolsMenu]     = useState(false);
  const initialCoverDataRef                   = useRef<any>(null);
  const [chapters, setChapters]               = useState<Chapter[]>([
    { id: 1, title: 'Chapter 1', blocks: [{ id: `block-${Date.now()}`,     type: 'text', content: '' }] },
    { id: 2, title: 'Chapter 2', blocks: [{ id: `block-${Date.now() + 1}`, type: 'text', content: '' }] },
  ]);
 
  const [notification, setNotification] = useState<{
    show: boolean; type: 'save' | 'publish' | 'unpublish' | 'republish' | 'tags' | 'error' | 'story-submitted';
    storyTitle?: string; errorMessage?: string; className?: string;
  }>({ show: false, type: 'save' });
 
  const [isChallenge]    = useState(location.state?.isChallenge || false);
  const [challengeType]  = useState(location.state?.challengeType || '');
  const [challengePrompt] = useState(location.state?.challengePrompt || '');
  const [challengeTitle] = useState(location.state?.challengeTitle || '');
  const [wordLimit]      = useState(location.state?.wordLimit || 0);
  const [challengeXP]    = useState(location.state?.challengeXP || 0);
 
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
 
  const [wordCount, setWordCount]         = useState(0);
  const [totalWordCount, setTotalWordCount] = useState(0);
  const prevWordCountRef     = useRef(totalWordCount);
  const initialWordCountRef  = useRef<number | null>(null);
  const [spellCheckEnabled, setSpellCheckEnabled] = useState(true);
 
  // ── Helpers ───────────────────────────────────────────────────────────────
  const countWordsInHtml = (html: string) => {
    const text = html.replace(/<[^>]*>/g, '');
    return text.trim().split(/\s+/).filter(w => w.length > 0).length;
  };
 
  const countWords = (chapter: Chapter) => {
    if (chapter.content) return countWordsInHtml(chapter.content);
    if (chapter.blocks) return chapter.blocks.reduce((t, b) => t + (b.type === 'text' && b.content ? countWordsInHtml(b.content) : 0), 0);
    return 0;
  };
 
  const addTextBlock = () => {
    const updated = [...chapters];
    updated[currentChapter] = {
      ...updated[currentChapter],
      blocks: [...(updated[currentChapter].blocks || []), { id: `block-${Date.now()}`, type: 'text', content: '' }],
    };
    setChapters(updated);
  };
 
  const addImageBlock = async (file: File) => {
    const blockId   = `block-${Date.now()}`;
    const blobUrl   = URL.createObjectURL(file); // shown instantly while uploading
    const caption   = file.name.replace(/\.[^/.]+$/, '');
 
    // Optimistic insert — kid sees the image immediately
    const insertBlock = (imageUrl: string, uploading = false) => {
      setChapters(prev => {
        const updated = [...prev];
        const existing = updated[currentChapter].blocks || [];
        // If block already exists, replace its imageUrl (post-upload swap)
        const idx = existing.findIndex(b => b.id === blockId);
        if (idx !== -1) {
          const blocks = [...existing];
          blocks[idx] = { ...blocks[idx], imageUrl, uploading } as any;
          updated[currentChapter] = { ...updated[currentChapter], blocks };
        } else {
          updated[currentChapter] = {
            ...updated[currentChapter],
            blocks: [...existing, { id: blockId, type: 'image' as const, imageUrl, caption, uploading } as any],
          };
        }
        return updated;
      });
    };
 
    // 1. Show immediately with blob URL
    insertBlock(blobUrl, true);
 
    // 2. Upload to Firebase Storage in background
    if (!currentUser) {
      // Guest mode — blob URL is all we have; warn on save
      console.warn('Guest mode: image will not persist after page refresh');
      insertBlock(blobUrl, false);
      return;
    }
 
    try {
      const storyFolder = storyIdRef.current || 'unsaved';
      const ext         = file.name.split('.').pop() || 'jpg';
      const storagePath = `story-images/${currentUser.uid}/${storyFolder}/${blockId}.${ext}`;
      const storageRef  = ref(storage, storagePath);
 
      await uploadBytes(storageRef, file, { contentType: file.type });
      const downloadUrl = await getDownloadURL(storageRef);
 
      // 3. Swap blob URL for permanent Storage URL
      URL.revokeObjectURL(blobUrl); // free memory
      insertBlock(downloadUrl, false);
    } catch (error) {
      console.error('Image upload failed:', error);
      // Leave blob URL so image stays visible this session
      insertBlock(blobUrl, false);
      setNotification({
        show: true,
        type: 'error',
        errorMessage: '⚠️ Image saved locally but failed to upload. It may disappear on refresh — try saving again.',
      });
    }
  };
 
  const deleteBlock = (blockId: string) => {
    const updated = [...chapters];
    updated[currentChapter] = { ...updated[currentChapter], blocks: (updated[currentChapter].blocks || []).filter(b => b.id !== blockId) };
    setChapters(updated);
  };
 
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const updated = [...chapters];
      const blocks  = updated[currentChapter].blocks || [];
      const oi = blocks.findIndex(b => b.id === active.id);
      const ni = blocks.findIndex(b => b.id === over.id);
      updated[currentChapter] = { ...updated[currentChapter], blocks: arrayMove(blocks, oi, ni) };
      setChapters(updated);
    }
  };
 
  const handleChallengeComplete = useCallback(() => {
    if (!challengeData || challengeCompleted) return;
    if (isChallengeCompleted(challengeData.type)) return;
    const success = completeChallenge(challengeData.type, challengeData.xp);
    if (success) { setChallengeCompleted(true); setShowSuccessModal(true); }
  }, [challengeData, challengeCompleted, isChallengeCompleted, completeChallenge, showSuccessModal]);
 
  const handleChapterComplete = useCallback(() => {
    incrementChaptersThisWeek();
    if (challengeData?.type === 'weekly' && challengeData.goal?.type === 'chapter') handleChallengeComplete();
  }, [incrementChaptersThisWeek, challengeData, handleChallengeComplete]);
 
  const handleCoverSave = (data: any) => {
    setCoverData(data);
    if (storyId && currentUser) {
      StoryService.updateStory(currentUser.uid, storyId, { thumbnail: data ? JSON.stringify(data) : null }).catch(console.error);
    }
  };
 
  const handleUpdateChapters = useCallback((newChapters: any) => {
    setChapters(newChapters.map((nc: any) => {
      const existing = chapters.find(c => c.id === nc.id);
      return { ...nc, blocks: existing?.blocks || [] };
    }));
  }, [chapters]);
 
  const addChapter = () => {
    const newChapter: Chapter = {
      id: chapters.length + 1,
      title: `Chapter ${chapters.length + 1}`,
      blocks: [{ id: `block-${Date.now()}`, type: 'text' as const, content: '' }],
    };
    setChapters([...chapters, newChapter]);
    setCurrentChapter(chapters.length);
  };
 
  // NEW
  const handlePrint = () => {
    const pw = window.open('', '_blank');
    if (!pw) { alert('Please allow popups to print'); return; }
    const studentName = currentUser?.displayName || 'Student Writer';

    const chaptersContent = chapters.map((ch, idx) => {
      let bodyHtml = '';
      if (ch.blocks) {
        ch.blocks.forEach(b => {
          if (b.type === 'text') bodyHtml += b.content || '';
          if (b.type === 'image') bodyHtml += `
            <div style="text-align:center;margin:24pt 0;page-break-inside:avoid">
              <img src="${b.imageUrl}" style="max-width:100%;max-height:5in;display:block;margin:0 auto"/>
              ${b.caption ? `<p style="font-size:9pt;font-style:italic;color:#666;margin-top:6pt">${b.caption}</p>` : ''}
            </div>`;
        });
      } else { bodyHtml = ch.content || ''; }

     return `
     <div style="page-break-before:${idx === 0 ? 'auto' : 'always'}">
       <!-- Running header — story title left, chapter right -->
       <div style="display:flex;justify-content:space-between;align-items:baseline;
                   border-bottom:1pt solid #ccc;padding-bottom:6pt;margin-bottom:0">
         <span style="font-size:9pt;color:#999;letter-spacing:1pt;text-transform:uppercase">
           ${storyTitle}
         </span>
         <span style="font-size:10pt;font-weight:bold;color:#333">${ch.title}</span>
       </div>
       <!-- Chapter drop — consistent top margin on all chapters -->
       <div style="padding-top:72pt;text-align:center;margin-bottom:36pt">
         <div style="font-size:9pt;letter-spacing:3pt;text-transform:uppercase;color:#999;margin-bottom:8pt">Chapter ${idx + 1}</div>
         <div style="font-size:18pt;font-weight:900;color:#1a1a1a;font-family:Georgia,serif">${ch.title}</div>
         <div style="width:32pt;height:1.5pt;background:#ccc;margin:14pt auto 0"></div>
       </div>
       <div class="chapter-body">${bodyHtml}</div>
     </div>`;
    }).join('');

    pw.document.write(`<!DOCTYPE html>
<html><head>
  <title>${storyTitle} — ${studentName}</title>
  <style>
    @page { margin: 1in 1in 1in 1in; }
    * { box-sizing: border-box; }
    body {
      font-family: Georgia, 'Times New Roman', serif;
      font-size: 12pt;
      line-height: 2.2;          /* double-space for annotations */
      color: #1a1a1a;
      margin: 0;
    }

    /* Cover page */
    .cover {
      min-height: 9in;
      display: flex; flex-direction: column;
      justify-content: center; align-items: center;
      text-align: center; page-break-after: always;
    }
    .cover h1 { font-size: 28pt; margin: 0 0 12pt; letter-spacing: -0.5pt; }
    .cover .byline { font-size: 14pt; color: #555; font-style: italic; margin: 0 0 8pt; }
    .cover .meta { font-size: 9pt; color: #999; letter-spacing: 2pt; text-transform: uppercase; margin-top: 48pt; }

    /* Chapter body */
    .chapter-body p { margin: 0 0 0 0; }  /* line-height handles spacing */
    .chapter-body h1, .chapter-body h2, .chapter-body h3 {
      font-size: 13pt; margin: 12pt 0 6pt;
    }

    /* Annotation margin lines — printed as faint ruled lines on the right */
    .annotation-col {
      position: fixed; right: 0; top: 0; bottom: 0;
      width: 2.2in;
      border-left: 1pt solid #e0e0e0;
      background: repeating-linear-gradient(
        to bottom,
        transparent, transparent 27.6pt,
        #f0f0f0 27.6pt, #f0f0f0 28pt
      );
      pointer-events: none;
    }

    /* Page numbers */
    @page { @bottom-center { content: counter(page); font-family: Georgia, serif; font-size: 9pt; color: #999; } }

    /* Print button — hidden on print */
    .print-btn {
      position: fixed; top: 16px; right: 16px;
      padding: 10px 20px; background: #6a37d4; color: white;
      border: none; border-radius: 9999px; font-size: 13px;
      font-weight: 700; cursor: pointer; font-family: sans-serif;
      box-shadow: 0 4px 12px rgba(106,55,212,0.3);
    }
    @media print { .print-btn { display: none; } .annotation-col { display: none; } }
  </style>
</head>
<body>
  <button class="print-btn" onclick="window.print()">🖨️ Print</button>

  <div class="cover">
    <h1>${storyTitle}</h1>
    <div class="byline">by ${studentName}</div>
    <div class="meta">Written in Scriblish · ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long' })}</div>
  </div>

  ${chaptersContent}
</body></html>`);

    pw.document.close();
    setTimeout(() => { pw.focus(); }, 300);
  };
 
  // ── Effects (identical logic to original) ─────────────────────────────────
  useEffect(() => {
    const load = async () => {
      if (!currentUser) return;
      try {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          const classIds = userDoc.data().classes || [];
          const cls = (await Promise.all(classIds.map(async (id: string) => {
            const d = await getDoc(doc(db, 'classes', id));
            return d.exists() ? { id: d.id, ...d.data() } : null;
          }))).filter(Boolean);
          setUserClasses(cls);
        }
      } catch (e) { console.error(e); }
    };
    load();
  }, [currentUser]);
 
  useEffect(() => {
    const load = async () => {
        if (!storyId || !selectedClassId) return;
        console.log('Loading feedback for storyId:', storyId);
        setLoadingFeedback(true);
        try {
            const d = await getDoc(doc(db, 'teacherFeedback', storyId));
            console.log('Feedback exists:', d.exists(), 'data:', d.data());
            if (d.exists()) setTeacherFeedback(d.data());
        } catch (e) { console.error(e); } finally { setLoadingFeedback(false); }
    };
    load();
}, [storyId, selectedClassId]);
 
  useEffect(() => {
    const t = setTimeout(() => {
      setWordCount(countWords(chapters[currentChapter]));
      setTotalWordCount(chapters.reduce((s, c) => s + countWords(c), 0));
    }, 100);
    return () => clearTimeout(t);
  }, [chapters, currentChapter]);
 
  useEffect(() => {
    if (challengeData && initialWordCountRef.current === null) initialWordCountRef.current = totalWordCount;
  }, [challengeData, totalWordCount]);
 
  useEffect(() => { if (location.state?.storyId) { hasInitializedWordCount.current = false; prevWordCountRef.current = 0; } }, [location.state?.storyId]);
  useEffect(() => { if (!location.state?.storyId) { hasInitializedWordCount.current = false; prevWordCountRef.current = 0; } }, []);
 
  useEffect(() => {
    if (prevWordCountRef.current > 0 && !hasInitializedWordCount.current) { hasInitializedWordCount.current = true; return; }
    if (!hasInitializedWordCount.current) { prevWordCountRef.current = totalWordCount; hasInitializedWordCount.current = true; return; }
    const diff = totalWordCount - prevWordCountRef.current;
    if (diff > 0) { addWords(diff); updateStreak(); }
    prevWordCountRef.current = totalWordCount;
  }, [totalWordCount, addWords, updateStreak]);
 
  useEffect(() => {
    if (!challengeData || challengeCompleted || challengeData.goal?.type !== 'words') return;
    const written = totalWordCount - (initialWordCountRef.current || 0);
    if (written >= challengeData.goal.target) handleChallengeComplete();
  }, [totalWordCount, challengeData, challengeCompleted, handleChallengeComplete]);
 
  useEffect(() => {
    const init = async () => {
      const params   = new URLSearchParams(location.search);
      const idToLoad = params.get('storyId') || location.state?.storyId;
      if (!idToLoad) { 
        setLoading(false); 
        if (stateClassId) setSelectedClassId(stateClassId);
        return; 
    }
      try {
        const story = await StoryService.getStory(currentUser?.uid || null, idToLoad);
        if (story) {
          if (story.isPublished) {
            setNotification({ show: true, type: 'error', errorMessage: '📚 This story is published! Unpublish it first to edit.' });
            setLoading(false);
            setTimeout(() => navigate('/my-stories'), 2000);
            return;
          }
          setStoryTitle(story.title || 'My Story');
          setAuthorName((story as any).authorName || currentUser?.displayName || '');
          setChapters((story.content?.chapters || [{ id: 1, title: 'Chapter 1', content: '' }, { id: 2, title: 'Chapter 2', content: '' }]).map(migrateChapterToBlocks));
          let cov = null;
          if (story.thumbnail) { try { cov = typeof story.thumbnail === 'string' ? JSON.parse(story.thumbnail) : story.thumbnail; } catch {} }
          setCoverData(cov);
          setPlanData((story as any).planData || null);
          setStoryId(story.id || null);
          storyIdRef.current = story.id || null;
          if ((story as any).planData?.characters) setCharacters((story as any).planData.characters);
          if ((story as any).classId) setSelectedClassId((story as any).classId);
          if (stateClassId) setSelectedClassId(stateClassId);
        }
      } catch (e) { console.error(e); }
      setLoading(false);
      hasInitializedWordCount.current = false;
      // Signal that initial load is done — autosave can now watch for changes
      setTimeout(() => { hasLoadedRef.current = true; }, 1000);
    };
    init();
  }, [currentUser, location.search, location.state]);
 
  useEffect(() => {
    if (assignmentPrompt && !storyId) setChapters([{ id: Date.now(), title: 'Chapter 1', content: assignmentPrompt }]);
  }, [assignmentPrompt]);
 
  useEffect(() => {
    const update = async () => {
      if (assignmentId && currentUser && !storyId) {
        try {
          await setDoc(doc(db, 'studentAssignments', `${currentUser.uid}_${assignmentId}`), { assignmentId, studentId: currentUser.uid, classId: selectedClassId, status: 'in_progress', startedAt: Timestamp.now() }, { merge: true });
        } catch (e) { console.error(e); }
      }
    };
    update();
  }, [assignmentId, currentUser, storyId, selectedClassId]);
 
  useEffect(() => { const h = () => setIsMobile(window.innerWidth < 768); window.addEventListener('resize', h); return () => window.removeEventListener('resize', h); }, []);
 
  // ESC exits distraction-free mode
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape' && distractionFree) setDistractionFree(false); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [distractionFree]);
 
  useEffect(() => { if (planData?.characters) setCharacters(planData.characters); }, [planData]);
  useEffect(() => { if (characters.length > 0 && planData) setPlanData({ ...planData, characters }); }, [characters]);
 
  // ── Autosave — fires 60s after last change, silently (no toast) ───────────
  useEffect(() => {
    // Don't autosave until story has fully loaded, and skip guest mode
    // (guest saves are cheap but we still want the same gate)
    if (!hasLoadedRef.current) return;
 
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
 
    autoSaveTimerRef.current = setTimeout(async () => {
      if (saving) return; // a manual save is already in flight
      setSaving(true);
      try {
        if (isGuestMode) {
          const wc = chapters.reduce((t, c) => t + (c.content?.split(/\s+/).filter(Boolean).length || 0), 0);
          if (storyId) await updateDraft(storyId, { title: storyTitle, content: JSON.stringify({ chapters }), wordCount: wc, coverData, planData });
          // Don't create new guest drafts on autosave — only on explicit save
        } else if (currentUser) {
          const thumbnail = coverData ? JSON.stringify(coverData) : null;
          const cleanPlan = planData ? Object.keys(planData).reduce((a, k) => { if (planData[k] !== undefined) a[k] = planData[k]; return a; }, {} as any) : {};
          if (storyId) {
            await StoryService.updateStory(currentUser.uid, storyId, {
              title: storyTitle,
              content: { chapters },
              thumbnail,
              planData: cleanPlan,
              authorName: authorName || currentUser?.displayName || 'Author',
              lastEditedAt: Timestamp.now(),
            });
            setLastSaved(new Date());
          }
          // No storyId yet = unsaved new story, don't auto-create
        }
      } catch (e) {
        console.warn('Autosave failed silently:', e);
        // Intentionally silent — user will see on next manual save
      }
      setSaving(false);
    }, 60_000); // 60 seconds after last keystroke
 
    return () => { if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current); };
  }, [chapters, storyTitle, authorName]);
 
  // ── Cover designer memoized ───────────────────────────────────────────────
  const coverDesignerComponent = useMemo(() => {
    if (activeTool !== 'cover') return null;
    if (coverDataLoading) return <FullScreenLoader label="Loading cover..." />;
    return (
      <Suspense fallback={<FullScreenLoader label="Loading Cover Designer..." />}>
        <CoverDesigner storyTitle={storyTitle} authorName="Author" onSave={handleCoverSave} onClose={() => setActiveTool(null)}
          onManualSave={async () => { if (storyId) await StoryService.updateStory(currentUser?.uid || '', storyId, { thumbnail: coverData ? JSON.stringify(coverData) : null }); }}
          initialCoverData={coverData} storyId={storyId} currentUser={currentUser} />
      </Suspense>
    );
  }, [activeTool, coverDataLoading, storyTitle, handleCoverSave, currentUser, storyId, coverData]);
 
  // ── Circuit breaker ───────────────────────────────────────────────────────
  const now = Date.now();
  if (now - lastRenderTime.current < 16) {
    if (++renderCountRef.current > 50) return (
      <div style={{ padding: 40, textAlign: 'center', color: C.error }}>
        <h2>⚠️ Render loop detected</h2>
        <button onClick={() => window.location.reload()} style={{ marginTop: 16, padding: '12px 24px', background: C.primary, color: '#fff', border: 'none', borderRadius: 9999, cursor: 'pointer', fontWeight: 700 }}>Refresh</button>
      </div>
    );
  } else { renderCountRef.current = 0; }
  lastRenderTime.current = now;
 
  if (loading) return <FullScreenLoader label="Loading your story..." />;
 
  // ── Save logic ────────────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true);
    try {
      if (isGuestMode) {
        const wc = chapters.reduce((t, c) => t + (c.content?.split(/\s+/).filter(Boolean).length || 0), 0);
        const draft = { title: storyTitle || 'Untitled Story', content: JSON.stringify({ chapters }), wordCount: wc, genre: planData?.genre || '', targetAudience: planData?.targetAudience || '', coverData, planData };
        try {
          if (storyId) { await updateDraft(storyId, draft); } else { const id = await saveDraft(draft); setStoryId(id as string); }
          setLastSaved(new Date()); setNotification({ show: true, type: 'save' });
        } catch (e) {
          if (e instanceof Error && e.message === 'GUEST_LIMIT_REACHED') setShowLimitGate(true);
          else setNotification({ show: true, type: 'error', errorMessage: 'Failed to save. Please try again.' });
        }
        setSaving(false); return;
      }
      const thumbnail = coverData ? JSON.stringify(coverData) : null;
      const cleanPlan  = planData ? Object.keys(planData).reduce((a, k) => { if (planData[k] !== undefined) a[k] = planData[k]; return a; }, {} as any) : {};
      const classId    = selectedClassId || null;
      if (!storyId) {
        const data: any = { title: storyTitle, type: 'regular-book', content: { chapters }, thumbnail: thumbnail ?? null, planData: cleanPlan, authorName: authorName || currentUser?.displayName || 'Author', isChallenge: challengeData?.isChallenge || false, classId, submittedAt: classId ? Timestamp.now() : null, status: 'draft', assignmentId: assignmentId || null, assignmentTitle: assignmentTitle || null };
        if (challengeData?.isChallenge) { data.challengeType = challengeData.type; data.challengePrompt = challengePrompt; if (challengeCompleted) data.challengeCompletedAt = Timestamp.now(); }
        const newId = await StoryService.createStory(currentUser!.uid, data);
        setStoryId(newId);
        storyIdRef.current = newId;
        storyIdRef.current = newId;
        navigate(`/write?storyId=${newId}`, { replace: true, state: location.state });
      } else {
        const data: any = { title: storyTitle, content: { chapters }, thumbnail: thumbnail || null, planData: cleanPlan, authorName: authorName || currentUser?.displayName || 'Author', isChallenge: challengeData?.isChallenge || false, classId: selectedClassId || deleteField(), lastEditedAt: Timestamp.now() };
        if (challengeData?.isChallenge) { data.challengeType = challengeData.type; data.challengePrompt = challengePrompt; }
        await StoryService.updateStory(currentUser!.uid, storyId, data);
      }
      setLastSaved(new Date()); setNotification({ show: true, type: 'save' });
      // Inline safety check (layers 1 & 2 only — no API call)
const saveIssues = runSaveCheck({
  title: storyTitle,
  description: '',
  content: { chapters },
});
setInlineSafetyIssues(saveIssues);
    } catch (e) {
      console.error(e); setNotification({ show: true, type: 'error', errorMessage: 'Failed to save. Please try again.' });
    }
    setSaving(false);
  };
 
  // ── Full-screen tool overrides ────────────────────────────────────────────
  if (activeTool === 'characters') return (
    <Suspense fallback={<FullScreenLoader label="Loading Characters..." />}>
      <CharacterSheet characters={characters}
        onAddCharacter={(c) => { const n = [...characters, c]; setCharacters(n); setPlanData({ ...planData, characters: n }); }}
        onUpdateCharacter={(id, c) => { const n = characters.map(x => x.id === id ? c : x); setCharacters(n); setPlanData({ ...planData, characters: n }); }}
        onDeleteCharacter={(id) => { const n = characters.filter(x => x.id !== id); setCharacters(n); setPlanData({ ...planData, characters: n }); }}
        onClose={() => setActiveTool(null)} />
    </Suspense>
  );
 
  if (activeTool === 'planner') return (
    <Suspense fallback={<FullScreenLoader label="Loading Story Planner..." />}>
      <EnhancedStoryPlanner onClose={() => setActiveTool(null)} onSave={(d) => setPlanData(d)} initialPlanData={planData || undefined} />
    </Suspense>
  );
 
  if (activeTool === 'chapters') return (
    <Suspense fallback={<FullScreenLoader label="Loading Chapter Master..." />}>
      <ChapterMaster
        book={chapters.map(c => ({ id: c.id, title: c.title, content: c.content || (c.blocks?.[0]?.content || '') }))}
        onUpdateBook={handleUpdateChapters} currentPageIndex={currentChapter}
        onNavigateToPage={(i) => { setCurrentChapter(i); setActiveTool(null); }}
        onClose={() => setActiveTool(null)}
        theme={{ primary: '#FF6B35', secondary: '#9B59B6', accent: '#F39C12', success: '#2ECC71' }}
        onSave={async () => {
          if (!storyId) { alert('Save your story first'); return; }
          await StoryService.updateStory(currentUser?.uid || null, storyId, { title: storyTitle, content: { chapters }, classId: selectedClassId || deleteField(), planData, thumbnail: coverData ? JSON.stringify(coverData) : null });
        }} />
    </Suspense>
  );
 
  if (coverDesignerComponent) return coverDesignerComponent;
 
  // ═════════════════════════════════════════════════════════════════════════
  // ── MOBILE VIEW ──────────────────────────────────────────────────────────
  // ═════════════════════════════════════════════════════════════════════════
  if (isMobile) {
    return (
      <>
        <SignupPrompt isOpen={showSignupPrompt} onClose={() => setShowSignupPrompt(false)} reason={promptReason} />
        <Notification isOpen={notification.show} onClose={() => setNotification({ ...notification, show: false })}
          type={notification.type} storyTitle={notification.storyTitle} wordCount={wordCount}
          totalWords={totalWordCount} errorMessage={notification.errorMessage} />
        {showPreview && <StoryPreview storyTitle={storyTitle} authorName={authorName || currentUser?.displayName || 'Author'} coverData={coverData} chapters={chapters} onClose={() => setShowPreview(false)} />}
          
 
        {/* Mobile tool overlays */}
        {activeTool && (
          <div style={{ position: 'fixed', inset: 0, background: 'white', zIndex: 9999 }}>
            {activeTool === 'characters' && <CharacterSheet characters={characters} onAddCharacter={c => setCharacters([...characters, c])} onUpdateCharacter={(id, c) => setCharacters(characters.map(x => x.id === id ? c : x))} onDeleteCharacter={id => setCharacters(characters.filter(x => x.id !== id))} onClose={() => setActiveTool(null)} />}
            {activeTool === 'planner'    && <EnhancedStoryPlanner onClose={() => setActiveTool(null)} onSave={d => setPlanData(d)} initialPlanData={planData || undefined} />}
            {activeTool === 'cover'      && <CoverDesigner storyTitle={storyTitle} authorName="Author" onSave={handleCoverSave} onClose={() => setActiveTool(null)} onManualSave={async () => {}} initialCoverData={coverData} />}
          </div>
        )}
 
        <div style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", minHeight: '100vh', background: C.surface, display: 'flex', flexDirection: 'column', paddingBottom: 58 }}>
 
          {/* Mobile header */}
<header style={{ background: C.surfaceLowest, borderBottom: `1px solid ${C.surfaceHighest}`, flexShrink: 0, zIndex: 50 }}>

{/* Row 1 — Logo + Save */}
<div style={{ padding: '0 16px', height: 56, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
    <span style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: 900, fontSize: 20, background: gradientPrimary, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', fontStyle: 'italic', letterSpacing: -0.5 }}>
        Scriblish
    </span>
    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <button onClick={() => setShowPreview(true)}
            style={{ width: 40, height: 40, borderRadius: '50%', background: C.secondaryContainer, border: 'none', cursor: 'pointer', fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            📖
        </button>
        <button onClick={handleSave} disabled={saving}
            style={{ ...pillBtn, background: gradientPrimary, color: '#fff', padding: '10px 22px', fontSize: 14, fontWeight: 800, opacity: saving ? 0.7 : 1 }}>
            {saving ? '…' : lastSaved ? '✓ Saved' : '💾 Save'}
        </button>
    </div>
</div>

{/* Row 2 — Story title */}
<div style={{ padding: '0 16px 10px' }}>
    <input type="text" value={storyTitle} onChange={e => setStoryTitle(e.target.value)}
        style={{ width: '100%', fontFamily: "'Epilogue', sans-serif", fontWeight: 800, fontSize: 18, color: C.onSurface, border: 'none', outline: 'none', background: 'transparent', boxSizing: 'border-box' as const }}
        placeholder="Story Title" />
</div>

{/* Row 3 — Chapter pills (write view only) */}
{mobileView === 'write' && (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0 16px 12px', overflowX: 'auto', scrollbarWidth: 'none' as const }}>
        {chapters.map((ch, i) => (
            <button key={ch.id} onClick={() => setCurrentChapter(i)} style={{
                ...pillBtn, flexShrink: 0, padding: '8px 16px', fontSize: 13, fontWeight: 700,
                background: currentChapter === i ? gradientPrimary : C.surfaceLow,
                color: currentChapter === i ? '#fff' : C.onSurfaceVariant, border: 'none',
                boxShadow: currentChapter === i ? `0 4px 12px ${C.primary}40` : 'none',
            }}>
                {ch.title}
            </button>
        ))}
        <button onClick={addChapter} style={{ ...pillBtn, flexShrink: 0, padding: '8px 14px', fontSize: 13, background: C.surfaceHigh, color: C.onSurfaceVariant, border: 'none' }}>
            + Chapter
        </button>
    </div>
)}
</header>
 
          {challengeData && (
            <ElegantChallengeBanner challengeType={challengeData.type} challengeTitle={challengeData.title || ''}
              challengePrompt={challengePrompt} currentWordCount={Math.max(0, totalWordCount - (initialWordCountRef.current || 0))}
              targetWordCount={challengeData.goal.target} xpReward={challengeData.xp} isMobile />
          )}
 
          {isGuestMode && (
            <div style={{ margin: '10px 12px', background: '#FFF4E6', border: '2px solid #FFB84D', borderRadius: 12, padding: '10px 14px', display: 'flex', gap: 10, alignItems: 'center', fontSize: 13 }}>
              <span>⚠️</span>
              <span style={{ flex: 1 }}><strong>Guest mode</strong> — saved on this device only.</span>
              <button onClick={() => navigate('/signup')} style={{ ...pillBtn, background: '#FF6B35', color: '#fff', padding: '6px 12px', fontSize: 12 }}>Sign Up</button>
            </div>
          )}

<SafetyInlineWarning
    issues={inlineSafetyIssues}
    onDismiss={() => setInlineSafetyIssues([])}
/>
 
          {/* Write view */}
          {mobileView === 'write' && (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>

        {/* Formatting bar — always visible, full width */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '8px 12px', background: C.surfaceLowest, borderBottom: `1px solid ${C.surfaceHigh}`, flexShrink: 0 }}>
            {[
                { label: 'B', style: { fontWeight: 800 }, fn: () => activeBlockEditorRef.current?.chain().focus().toggleBold().run(), active: () => activeBlockEditorRef.current?.isActive('bold') },
                { label: 'I', style: { fontStyle: 'italic' as const }, fn: () => activeBlockEditorRef.current?.chain().focus().toggleItalic().run(), active: () => activeBlockEditorRef.current?.isActive('italic') },
                { label: 'U', style: { textDecoration: 'underline' as const }, fn: () => activeBlockEditorRef.current?.chain().focus().toggleUnderline().run(), active: () => activeBlockEditorRef.current?.isActive('underline') },
            ].map((b, i) => (
                <button key={i} onClick={b.fn} style={{
                    ...b.style, width: 44, height: 44, borderRadius: 10, border: 'none', cursor: 'pointer', fontSize: 16, fontWeight: 700,
                    background: b.active() ? C.primaryContainer : 'transparent',
                    color: b.active() ? C.primary : C.onSurfaceVariant,
                }}>
                    {b.label}
                </button>
            ))}

            <div style={{ width: 1, height: 24, background: C.outlineVariant, margin: '0 4px', opacity: 0.4 }} />

            {/* Add blocks inline */}
            <button onClick={addTextBlock}
                style={{ ...pillBtn, background: C.primaryContainer, color: C.primary, padding: '8px 14px', fontSize: 12, fontWeight: 700, border: `1.5px solid ${C.primaryLight}` }}>
                ➕ Text
            </button>
            <label style={{ ...pillBtn, background: C.secondaryContainer, color: C.secondary, padding: '8px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer', userSelect: 'none' as const, border: `1.5px solid ${C.secondaryDim}` }}>
                📸 Photo<input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => { const f = e.target.files?.[0]; if (f) addImageBlock(f); }} />
            </label>
        </div>

        {/* Scrollable writing area */}
        <div spellCheck={spellCheckEnabled} style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={chapters[currentChapter]?.blocks?.map(b => b.id) || []} strategy={verticalListSortingStrategy}>
                    {chapters[currentChapter]?.blocks?.map(block => (
                        <SortableBlock key={block.id} id={block.id}>
                            {block.type === 'text'
                                ? <TextBlockComponent key={`${currentChapter}-${block.id}`} block={block}
                                    chapterIndex={currentChapter}
                                    onUpdate={content => { const u = [...chapters]; const bi = u[currentChapter].blocks?.findIndex(b => b.id === block.id); if (bi !== undefined && bi !== -1 && u[currentChapter].blocks) { u[currentChapter].blocks![bi].content = content; setChapters(u); } }}
                                    onDelete={() => deleteBlock(block.id)}
                                    onFocus={editor => { activeBlockEditorRef.current = editor; forceUpdate(n => n + 1); }} />
                                : <ImageBlockComponent block={block} onDelete={() => deleteBlock(block.id)} />}
                        </SortableBlock>
                    ))}
                </SortableContext>
            </DndContext>
        </div>
    </div>
)}
 
          {/* Tools view */}
          {mobileView === 'tools' && (
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 14px' }}>
              <p style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: 800, fontSize: 18, color: C.onSurface, marginBottom: 16 }}>Creative Tools</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {mobileTools.map(tool => (
                  <button key={tool.id} onClick={() => tool.id === 'print' ? handlePrint() : setActiveTool(tool.id)}
                    style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '18px 20px', background: tool.bg, border: `2px solid ${tool.border}`, borderRadius: 20, cursor: 'pointer', textAlign: 'left' }}>
                    <span style={{ fontSize: 36 }}>{tool.icon}</span>
                    <div>
                      <div style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: 800, fontSize: 16, color: C.onSurface }}>{tool.title}</div>
                      <div style={{ fontSize: 13, color: C.onSurfaceVariant, marginTop: 2 }}>{tool.desc}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
 
          {/* Settings view */}
          {mobileView === 'settings' && (
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 14px' }}>
              <p style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: 800, fontSize: 18, color: C.onSurface, marginBottom: 16 }}>Settings</p>
              <div style={{ background: C.surfaceLowest, borderRadius: 16, padding: 20 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', fontSize: 15, color: C.onSurface }}>
                  <input type="checkbox" checked={spellCheckEnabled} onChange={e => setSpellCheckEnabled(e.target.checked)} style={{ width: 20, height: 20 }} />
                  Spell check while writing
                </label>
              </div>
            </div>
          )}
 
          {/* Mobile bottom nav */}
          <nav style={{ position: 'fixed', bottom: 0, left: 0, right: 0, background: 'rgba(255,255,255,0.96)', backdropFilter: 'blur(20px)', borderTop: `1px solid ${C.surfaceHighest}`, display: 'flex', justifyContent: 'space-around', padding: '10px 0 16px', zIndex: 150 }}>
    {[
        { id: 'write', icon: '✍️', label: 'Write' },
        { id: 'tools', icon: '🛠️', label: 'Tools' },
        { id: 'settings', icon: '⚙️', label: 'More' },
    ].map(item => (
        <button key={item.id} onClick={() => setMobileView(item.id as any)}
            style={{ background: 'none', border: 'none', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, cursor: 'pointer', flex: 1, padding: '4px 0' }}>
            <div style={{ width: 44, height: 44, borderRadius: 14, background: mobileView === item.id ? C.primaryContainer : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, transition: 'background 0.15s' }}>
                {item.icon}
            </div>
            <span style={{ fontSize: 11, fontWeight: 700, fontFamily: "'Plus Jakarta Sans', sans-serif", color: mobileView === item.id ? C.primary : C.outlineVariant }}>
                {item.label}
            </span>
        </button>
    ))}
</nav>
        </div>
 
        {showSuccessModal && <ChallengeSuccessModal challengeType={challengeData!.type} xpEarned={challengeData!.xp} currentXP={xp} currentLevel={level} onClose={() => setShowSuccessModal(false)} />}
        {showLimitGate && <RegistrationGate type="limit-reached" onRegister={() => navigate('/signup')} />}
 
        <style>{globalStyles}</style>
      </>
    );
  }
 
  // ═════════════════════════════════════════════════════════════════════════
  // ── DESKTOP VIEW ─────────────────────────────────────────────────────────
  // ═════════════════════════════════════════════════════════════════════════
  return (
    <>
      <SignupPrompt isOpen={showSignupPrompt} onClose={() => setShowSignupPrompt(false)} reason={promptReason} />
      <Notification isOpen={notification.show} onClose={() => setNotification({ ...notification, show: false })}
        type={notification.type} storyTitle={notification.storyTitle} wordCount={wordCount}
        totalWords={totalWordCount} errorMessage={notification.errorMessage} className={notification.className} />
      {showPreview && <StoryPreview storyTitle={storyTitle} authorName={authorName || currentUser?.displayName || 'Author'} coverData={coverData} chapters={chapters} onClose={() => setShowPreview(false)} />}
 
      <div style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", height: '100vh', display: 'flex', flexDirection: 'column', background: C.surface, overflow: 'hidden' }}>
 
        {/* ── Top App Bar ── */}
        <header style={{ background: C.surfaceLow, borderBottom: `1px solid ${C.surfaceHighest}`, padding: '0 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 64, flexShrink: 0, zIndex: 50 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <button onClick={() => setSidebarCollapsed(!sidebarCollapsed)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.primary, padding: 8, borderRadius: 8, display: 'flex' }}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <rect y="3" width="20" height="2" rx="1" fill="currentColor"/>
                <rect y="9" width="14" height="2" rx="1" fill="currentColor"/>
                <rect y="15" width="20" height="2" rx="1" fill="currentColor"/>
              </svg>
            </button>
            <span style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: 900, fontSize: 20, background: gradientPrimary, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', fontStyle: 'italic', letterSpacing: -0.5 }}>
              Scriblish
            </span>
          </div>
 
          {/* Title input */}
          <input type="text" value={storyTitle} onChange={e => setStoryTitle(e.target.value)}
            style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: 800, fontSize: 20, color: C.onSurface, border: 'none', outline: 'none', background: 'transparent', flex: 1, margin: '0 12px'}}
            placeholder="Untitled Story" />
 
          {/* Class / assignment indicator */}
          {!assignmentId && userClasses.length > 0 && (
    <select value={selectedClassId || ''} onChange={e => setSelectedClassId(e.target.value || null)}
        style={{ marginRight: 12, padding: '8px 14px', borderRadius: 9999, border: `2px solid ${C.outlineVariant}`, fontSize: 13, fontWeight: 600, color: C.onSurfaceVariant, background: C.surfaceLowest, cursor: 'pointer', flexShrink: 0 }}>
        <option value="">📝 Personal Story</option>
        {userClasses.map((c: any) => <option key={c.id} value={c.id}>📚 {c.name}</option>)}
    </select>
)}
 
          {/* Action buttons */}
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexShrink: 0 }}>
      
<button onClick={() => setShowPreview(true)} title="See your finished book — export to PDF or print via Blurb"
  style={{ ...pillBtn, background: `linear-gradient(135deg, ${C.secondary}, #00a86b)`, color: '#fff', padding: '9px 18px', fontWeight: 700, fontSize: 14, boxShadow: `0 4px 12px ${C.secondary}40` }}>
  📖 Publish Preview
</button>
            {selectedClassId && (
              <button onClick={handleSubmitToClass} style={{ ...pillBtn, background: `linear-gradient(135deg, ${C.secondary}, ${C.secondaryDim})`, color: '#fff', padding: '9px 18px', fontWeight: 700, fontSize: 14 }}>
                📤 Submit
              </button>
            )}
            {selectedClassId && storyId && (
              <button onClick={() => setShowFeedbackPanel(!showFeedbackPanel)}
                style={{ ...pillBtn, background: showFeedbackPanel ? gradientPrimary : C.surfaceLowest, color: showFeedbackPanel ? '#fff' : C.primary, border: `2px solid ${C.primary}`, padding: '9px 18px', fontWeight: 700, fontSize: 14 }}>
                💬 Feedback
              </button>
            )}
            <button onClick={handleSave} disabled={saving} style={{ ...pillBtn, background: gradientPrimary, color: '#fff', padding: '9px 22px', fontWeight: 700, fontSize: 14, opacity: saving ? 0.7 : 1 }}>
              {saving ? 'Saving…' : lastSaved ? '✓ Saved' : '💾 Save'}
            </button>
          </div>
        </header>
 
        <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
 
          {/* ── Left Sidebar ── */}
          {!sidebarCollapsed && !distractionFree && (
            <aside style={{ width: 272, background: C.surfaceLowest, borderRight: `1px solid ${C.surfaceHighest}`, display: 'flex', flexDirection: 'column', overflowY: 'auto', flexShrink: 0 }}>
 
              {/* Chapters section */}
              <section style={{ padding: '20px 16px', borderBottom: `1px solid ${C.surfaceContainer}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <button onClick={() => setChaptersOpen(!chaptersOpen)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: 800, fontSize: 14, color: C.onSurface }}>Chapters</span>
                    <span style={{ fontSize: 11, color: C.onSurfaceVariant, transform: chaptersOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform .2s', display: 'inline-block' }}>▼</span>
                  </button>
                  <button onClick={addChapter} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.primary, display: 'flex', alignItems: 'center' }} title="Add chapter">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><circle cx="10" cy="10" r="9" stroke="currentColor" strokeWidth="1.5"/><path d="M10 6v8M6 10h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                  </button>
                </div>
 
                {chaptersOpen && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {chapters.map((ch, i) => (
                      <div key={ch.id}
                        className="chapter-row"
                        onClick={() => setCurrentChapter(i)}
                        style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          width: '100%', padding: '10px 12px', borderRadius: 12,
                          background: currentChapter === i ? C.primaryContainer : 'transparent',
                          borderLeft: `3px solid ${currentChapter === i ? C.primary : 'transparent'}`,
                          cursor: 'pointer', transition: 'all .15s', position: 'relative',
                          boxSizing: 'border-box',
                        }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <input
                            type="text" value={ch.title}
                            onClick={e => { e.stopPropagation(); setCurrentChapter(i); }}
                            onChange={e => { const u = [...chapters]; u[i] = { ...u[i], title: e.target.value }; setChapters(u); }}
                            style={{ background: 'transparent', border: 'none', outline: 'none', fontWeight: 700, fontSize: 13, color: currentChapter === i ? C.primary : C.onSurface, width: '100%', cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                          />
                          <div style={{ fontSize: 11, color: C.onSurfaceVariant, marginTop: 2 }}>{countWords(ch)} words</div>
                        </div>
                        {/* Delete — only shown on hover via CSS class, disabled for last chapter */}
                        {chapters.length > 1 && (
                          <button
                            className="chapter-delete-btn"
                            onClick={e => {
                              e.stopPropagation();
                              if (!window.confirm(`Delete "${ch.title}"? This can't be undone.`)) return;
                              const updated = chapters.filter((_, idx) => idx !== i);
                              // Renumber IDs sequentially
                              const renumbered = updated.map((c, idx) => ({ ...c, id: idx + 1 }));
                              setChapters(renumbered);
                              setCurrentChapter(Math.min(currentChapter, renumbered.length - 1));
                            }}
                            title="Delete chapter"
                            style={{
                              background: '#fff0f3', color: C.error, border: `1.5px solid #ffc8d3`,
                              borderRadius: 7, padding: '3px 7px', cursor: 'pointer', fontSize: 11,
                              fontWeight: 700, flexShrink: 0, opacity: 0,
                              transition: 'opacity .15s',
                            }}
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </section>
 
              {/* Creative tools bento */}
              <section style={{ padding: '20px 16px', flex: 1 }}>
                <p style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: 800, fontSize: 14, color: C.onSurface, marginBottom: 12 }}>Creative Tools</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {desktopTools.map(tool => (
                    <button key={tool.id} onClick={() => setActiveTool(tool.id)}
                      style={{ position: 'relative', overflow: 'hidden', padding: '14px 16px', background: tool.bg, borderRadius: 16, border: 'none', cursor: 'pointer', textAlign: 'left', transition: 'transform .15s, box-shadow .15s' }}
                      onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(-2px)'; (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 6px 20px rgba(0,0,0,0.1)'; }}
                      onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.transform = 'none'; (e.currentTarget as HTMLButtonElement).style.boxShadow = 'none'; }}>
                      <div style={{ position: 'absolute', right: -8, bottom: -8, fontSize: 56, opacity: 0.12, pointerEvents: 'none' }}>{tool.icon}</div>
                      <span style={{ fontSize: 20, display: 'block', marginBottom: 4 }}>{tool.icon}</span>
                      <div style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: 800, fontSize: 13, color: tool.textColor }}>{tool.title}</div>
                      <div style={{ fontSize: 11, color: tool.textColor, opacity: 0.7, marginTop: 2 }}>{tool.desc}</div>
                    </button>
                  ))}
 

<button onClick={handlePrint} title="Double-spaced draft with annotation margins — for pen & paper edits"
  style={{ padding: '12px 16px', background: C.surfaceContainer, borderRadius: 16, border: 'none', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 10 }}>
  <span style={{ fontSize: 20 }}>✏️</span>
  <div>
    <div style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: 800, fontSize: 13, color: C.onSurface }}>Print Draft</div>
    <div style={{ fontSize: 11, color: C.onSurfaceVariant }}>Double-spaced · for marking up</div>
  </div>
</button>
                </div>
              </section>
            </aside>
          )}
 
          {/* ── Editor Canvas ── */}
          <main style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: '#fafcff' }}>
 
            {/* Glass formatting toolbar — hidden in distraction-free */}
            {!distractionFree && (
            <div style={{
              background: 'rgba(243,246,255,0.85)', backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)',
              borderBottom: `1px solid ${C.surfaceHighest}`, padding: '10px 24px',
              display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0, zIndex: 20,
            }}>
              {[
                { label: <strong>B</strong>, fn: () => activeBlockEditorRef.current?.chain().focus().toggleBold().run(),      active: () => activeBlockEditorRef.current?.isActive('bold'),      title: 'Bold' },
                { label: <em>I</em>,         fn: () => activeBlockEditorRef.current?.chain().focus().toggleItalic().run(),    active: () => activeBlockEditorRef.current?.isActive('italic'),    title: 'Italic' },
                { label: <u>U</u>,           fn: () => activeBlockEditorRef.current?.chain().focus().toggleUnderline().run(), active: () => activeBlockEditorRef.current?.isActive('underline'), title: 'Underline' },
              ].map((b, i) => (
                <button key={i} onClick={b.fn} title={b.title} style={{
                  padding: '7px 12px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 15,
                  background: b.active() ? C.primaryContainer : 'transparent',
                  color: b.active() ? C.primary : C.onSurfaceVariant,
                  transition: 'all .15s',
                }}>{b.label}</button>
              ))}
              <div style={{ width: 1, height: 22, background: C.outlineVariant, margin: '0 6px', opacity: 0.4 }} />
              <button onClick={() => setSpellCheckEnabled(!spellCheckEnabled)} title="Spell Check"
                style={{ padding: '7px 12px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 700, background: spellCheckEnabled ? C.primaryContainer : 'transparent', color: spellCheckEnabled ? C.primary : C.onSurfaceVariant }}>
                ABC {spellCheckEnabled ? '✓' : '✗'}
              </button>
 
              <div style={{ flex: 1 }} />
 
              {/* Focus mode toggle */}
              <button onClick={() => setDistractionFree(true)} title="Focus mode — hides sidebar and toolbar (Esc to exit)"
                style={{ ...pillBtn, background: 'transparent', color: C.onSurfaceVariant, padding: '7px 14px', fontSize: 13, fontWeight: 700, border: `2px solid transparent` }}
                onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = C.outlineVariant; }}
                onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = 'transparent'; }}>
                ⛶ Focus
              </button>
              <div style={{ width: 1, height: 22, background: C.outlineVariant, margin: '0 6px', opacity: 0.4 }} />
 
              {/* Add blocks */}
              <button onClick={addTextBlock} style={{ ...pillBtn, background: C.primaryContainer, color: C.primary, padding: '7px 16px', fontSize: 13, fontWeight: 700 }}>➕ Text</button>
              <label style={{ ...pillBtn, background: C.secondaryContainer, color: C.secondary, padding: '7px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer', userSelect: 'none' }}>
                📸 Image<input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => { const f = e.target.files?.[0]; if (f) addImageBlock(f); }} />
              </label>
            </div>
            )}

             {/* Assignment banner */}
             {assignmentId && assignmentTitle && (
                <div style={{ background: C.primaryContainer, borderBottom: `1.5px solid ${C.primaryLight}40`, padding: '8px 24px', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                    <span style={{ fontSize: 13 }}>📝</span>
                    <div style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: 700, fontSize: 13, color: C.primary, flex: 1 }}>{assignmentTitle}</div>
                    <select value={selectedClassId || ''} onChange={e => setSelectedClassId(e.target.value || null)}
                        style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 12, color: C.onSurfaceVariant, cursor: 'pointer', fontWeight: 600 }}>
                        {userClasses.map((c: any) => <option key={c.id} value={c.id}>📚 {c.name}</option>)}
                    </select>
                </div>
            )}
 
            {challengeData && (
              <ElegantChallengeBanner challengeType={challengeData.type} challengeTitle={challengeData.title || ''}
                challengePrompt={challengePrompt} currentWordCount={Math.max(0, totalWordCount - (initialWordCountRef.current || 0))}
                targetWordCount={challengeData.goal.target} xpReward={challengeData.xp} isMobile={false} />
            )}

<SafetyInlineWarning
    issues={inlineSafetyIssues}
    onDismiss={() => setInlineSafetyIssues([])}
/>
 
            {isGuestMode && (
              <div style={{ margin: '12px 24px 0', background: '#FFF4E6', border: '2px solid #FFB84D', borderRadius: 12, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12, fontSize: 14 }}>
                <span>⚠️</span>
                <span style={{ flex: 1 }}><strong>Guest mode</strong> — stories saved on this device only.</span>
                <button onClick={() => navigate('/signup')} style={{ ...pillBtn, background: '#FF6B35', color: '#fff', padding: '7px 16px', fontSize: 13 }}>Sign Up Free</button>
              </div>
            )}
 
            {/* Scrollable editor area */}
            <div spellCheck={spellCheckEnabled} style={{ flex: 1, overflowY: 'auto', padding: distractionFree ? '48px 80px' : '32px 48px', width: '100%', boxSizing: 'border-box', transition: 'padding .3s' }}>
              {/* Distraction-free exit button — floats top-right of writing area */}
              {distractionFree && (
                <div style={{ position: 'fixed', top: 20, right: 24, zIndex: 100 }}>
                  <button onClick={() => setDistractionFree(false)}
                    style={{ ...pillBtn, background: 'rgba(37,47,60,0.75)', backdropFilter: 'blur(12px)', color: '#fff', padding: '8px 18px', fontSize: 13, fontWeight: 700, border: '1.5px solid rgba(255,255,255,0.15)' }}>
                    ⛶ Exit Focus <span style={{ opacity: 0.6, fontSize: 11, marginLeft: 4 }}>Esc</span>
                  </button>
                </div>
              )}
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={chapters[currentChapter]?.blocks?.map(b => b.id) || []} strategy={verticalListSortingStrategy}>
                  {chapters[currentChapter]?.blocks?.map(block => (
                    <SortableBlock key={block.id} id={block.id}>
                      {block.type === 'text'
                        ? <TextBlockComponent key={`${currentChapter}-${block.id}`} block={block}
                            chapterIndex={currentChapter}
                            onUpdate={content => { const u = [...chapters]; const bi = u[currentChapter].blocks?.findIndex(b => b.id === block.id); if (bi !== undefined && bi !== -1 && u[currentChapter].blocks) { u[currentChapter].blocks![bi].content = content; setChapters(u); } }}
                            onDelete={() => deleteBlock(block.id)}
                            onFocus={editor => { activeBlockEditorRef.current = editor; forceUpdate(n => n + 1); }} />
                        : <ImageBlockComponent block={block} onDelete={() => deleteBlock(block.id)} />}
                    </SortableBlock>
                  ))}
                </SortableContext>
              </DndContext>
            </div>
          </main>
 
          {/* Feedback panel */}
          {showFeedbackPanel && selectedClassId && <FeedbackPanel teacherFeedback={teacherFeedback} loadingFeedback={loadingFeedback} onClose={() => setShowFeedbackPanel(false)} storyId={storyId} currentUser={currentUser} setTeacherFeedback={setTeacherFeedback} StoryService={StoryService} />}
        </div>
 
        {/* Floating status pill */}
        <div style={{ position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)', zIndex: 40 }}>
          <div style={{ background: 'rgba(37,47,60,0.95)', backdropFilter: 'blur(16px)', borderRadius: 9999, padding: '10px 24px', display: 'flex', alignItems: 'center', gap: 20, color: '#fff', fontSize: 13, fontWeight: 600, boxShadow: '0 8px 32px rgba(0,0,0,0.25)', whiteSpace: 'nowrap' }}>
            <span style={{ color: C.primaryLight }}>✍️ <strong>{wordCount}</strong> words</span>
            <div style={{ width: 1, height: 16, background: 'rgba(255,255,255,0.2)' }} />
            <span style={{ color: C.secondaryDim }}>📚 <strong>{totalWordCount.toLocaleString()}</strong> total</span>
            <div style={{ width: 1, height: 16, background: 'rgba(255,255,255,0.2)' }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ height: 6, width: 80, background: 'rgba(255,255,255,0.15)', borderRadius: 3, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${Math.min(100, (xp % 500) / 5)}%`, background: gradientPrimary, borderRadius: 3 }} />
              </div>
              <span style={{ color: C.primaryLight }}>Lv {level}</span>
            </div>
            <div style={{ width: 1, height: 16, background: 'rgba(255,255,255,0.2)' }} />
            <span style={{ color: '#FF9F43' }}>🔥 {streak}</span>
            <div style={{ width: 1, height: 16, background: 'rgba(255,255,255,0.2)' }} />
            <span style={{ color: C.secondaryDim, fontSize: 12 }}>
              {saving ? '💾 Saving…' : lastSaved ? `☁️ Saved ${lastSaved.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : '☁️ Not yet saved'}
            </span>
          </div>
        </div>
      </div>
 
      {showSuccessModal && <ChallengeSuccessModal challengeType={challengeData!.type} xpEarned={challengeData!.xp} currentXP={xp} currentLevel={level} onClose={() => setShowSuccessModal(false)} />}
      {showLimitGate && <RegistrationGate type="limit-reached" onRegister={() => navigate('/signup')} />}
 
      <style>{globalStyles}</style>
    </>
  );
 
  // ── Submit helper ─────────────────────────────────────────────────────────
  async function handleSubmitToClass() {
    let sid = storyId;
    if (!sid) {
        // Only save if story doesn't exist yet
        await handleSave();
        await new Promise(r => setTimeout(r, 500));
        console.log('storyIdRef after save:', storyIdRef.current);
        console.log('storyId state after save:', storyId);
        sid = storyIdRef.current;
        if (!sid) {
            const q = query(collection(db, 'stories'), where('userId', '==', currentUser!.uid), where('assignmentId', '==', assignmentId), orderBy('createdAt', 'desc'), limit(1));
            const snap = await getDocs(q);
            if (!snap.empty) sid = snap.docs[0].id;
        }
    }
    if (!sid) return;
    console.log('About to submit with sid:', sid, 'classId:', selectedClassId);
    await StoryService.updateStory(currentUser!.uid, sid, {
        status: 'published',
        submittedAt: Timestamp.now(),
        classId: selectedClassId as string,
    });
    if (assignmentId) {
        await setDoc(doc(db, 'studentAssignments', `${currentUser!.uid}_${assignmentId}`), { assignmentId, studentId: currentUser!.uid, classId: selectedClassId, status: 'submitted', storyId: sid, submittedAt: Timestamp.now() }, { merge: true });
    }
    setNotification({ show: true, type: 'story-submitted', storyTitle, className: userClasses.find((c: any) => c.id === selectedClassId)?.className });
}
};
 
// ─── Shared style helpers ─────────────────────────────────────────────────────
const pillBtn: React.CSSProperties = {
  borderRadius: 9999,
  border: 'none',
  cursor: 'pointer',
  fontFamily: "'Plus Jakarta Sans', sans-serif",
  fontWeight: 700,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  transition: 'opacity .15s, transform .1s',
};
 
const globalStyles = `
  .ProseMirror {
    font-family: Georgia, 'Times New Roman', serif;
    font-size: 18px;
    line-height: 1.85;
    outline: none;
    min-height: 200px;
    color: #252f3c;
  }
  .ProseMirror p { margin: 0 0 12px; }
  .ProseMirror p.is-editor-empty:first-child::before {
    content: 'Once upon a time…';
    color: #a4aebe;
    font-style: italic;
    pointer-events: none;
    float: left;
    height: 0;
  }
  .ProseMirror strong { font-weight: 700; }
  .ProseMirror em { font-style: italic; }
  .ProseMirror u { text-decoration: underline; }
  .chapter-row:hover { background: #eaf1ff !important; }
  .chapter-row:hover .chapter-delete-btn { opacity: 1 !important; }
  @keyframes fadeSlideIn {
    from { opacity: 0; transform: translateY(-6px); }
    to   { opacity: 1; transform: translateY(0); }
  }
`;
 
// ─── Story starter prompts ────────────────────────────────────────────────────
const STORY_STARTERS = {
  opening: [
    "The day everything changed started like any other…",
    "Nobody believed me when I said I found a door that wasn't there yesterday.",
    "My best friend dared me to open the old trunk in the attic. I should have said no.",
    "The letter arrived on my birthday — but it was from my future self.",
    "I'd always been told the forest was dangerous. Today I was going to find out why.",
    "It was the smell that told me something was very wrong.",
    "The robot sat at the lunch table next to me and said, 'I think I need your help.'",
    "Everyone at school had a superpower. Everyone except me. Or so I thought.",
    "The map had been in our family for a hundred years. Tonight, I finally knew what it meant.",
    "When the new kid moved in next door, I noticed she never cast a shadow.",
  ],
  continuation: [
    "Just when things couldn't get any stranger, they did.",
    "I had about thirty seconds to make the most important decision of my life.",
    "The clue had been right in front of me the whole time.",
    "That's when the second surprise arrived — bigger than the first.",
    "We hadn't counted on running into them here, of all places.",
    "Something about the story didn't add up, and I was determined to find out what.",
    "There was only one person who could help me now.",
    "The plan had been perfect. Until it wasn't.",
    "Looking back, that was the moment everything started to make sense.",
    "I took a deep breath and stepped through the door.",
  ],
};
 
const getStarters = (chapterIndex: number): string[] => {
  const pool = chapterIndex === 0 ? STORY_STARTERS.opening : STORY_STARTERS.continuation;
  // Pick 3 random non-repeating prompts
  const shuffled = [...pool].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, 3);
};
 
// ─── Tool data ────────────────────────────────────────────────────────────────
const desktopTools = [
  { id: 'characters', icon: '🎭', title: 'Character Lab',   desc: "Build your hero's soul",   bg: '#ede8ff', textColor: '#370086' },
  { id: 'planner',    icon: '🌱', title: 'Story Seeds',     desc: 'Plan your plot beats',      bg: '#fed01b', textColor: '#433500' },
  { id: 'cover',      icon: '🎨', title: 'Cover Designer',  desc: 'Make it look amazing',      bg: '#d4f9e9', textColor: '#004d30' },
];
 
const mobileTools = [
  { id: 'characters', icon: '🎭', title: 'Character Lab',   desc: "Build your hero's soul",   bg: '#ede8ff', border: '#6a37d4' },
  { id: 'planner',    icon: '🌱', title: 'Story Seeds',     desc: 'Plan your plot beats',      bg: '#fff9c4', border: '#6f5900' },
  { id: 'cover',      icon: '🎨', title: 'Cover Designer',  desc: 'Make it look amazing',      bg: '#d4f9e9', border: '#006947' },
  { id: 'print', icon: '✏️', title: 'Print Draft', desc: 'Double-spaced for editing', bg: '#f3f6ff', border: '#a4aebe' },
];
 
// ─── Feedback panel ───────────────────────────────────────────────────────────
const FeedbackPanel: React.FC<any> = ({ teacherFeedback, loadingFeedback, onClose, storyId, currentUser, setTeacherFeedback, StoryService }) => (
  <div style={{ width: 380, background: '#fff', borderLeft: `1px solid #cfdef5`, display: 'flex', flexDirection: 'column', animation: 'slideIn .3s ease-out', flexShrink: 0, overflowY: 'auto' }}>
    <div style={{ padding: '20px 20px 16px', borderBottom: '1px solid #eaf1ff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <span style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: 800, fontSize: 16, color: '#252f3c' }}>💬 Teacher Feedback</span>
      <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#525c6a', lineHeight: 1 }}>×</button>
    </div>
    <div style={{ flex: 1, padding: 20, overflowY: 'auto' }}>
      {loadingFeedback ? (
        <div style={{ textAlign: 'center', color: '#a4aebe', padding: '40px 0' }}>⏳ Loading…</div>
      ) : !teacherFeedback ? (
        <div style={{ textAlign: 'center', color: '#a4aebe', padding: '40px 0' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>✨</div>
          <p style={{ fontWeight: 700 }}>No feedback yet</p>
          <p style={{ fontSize: 13 }}>Your teacher will review soon!</p>
        </div>
      ) : (
        <>
          {teacherFeedback.status === 'returned' && (
            <div style={{ background: '#FEF3C7', border: '2px solid #F59E0B', borderRadius: 12, padding: 16, marginBottom: 20 }}>
              <div style={{ fontWeight: 700, color: '#92400E', marginBottom: 6 }}>⚠️ Returned for Revision</div>
              <p style={{ fontSize: 13, color: '#78350F', margin: 0 }}>Make changes, then resubmit.</p>
            </div>
          )}
          {teacherFeedback.publicComment && (
            <div style={{ background: '#f3f6ff', borderRadius: 12, padding: 16, marginBottom: 20, border: '2px solid #dce9fd' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#525c6a', marginBottom: 8 }}>From {teacherFeedback.teacherName || 'Your Teacher'}</div>
              <p style={{ fontSize: 15, color: '#252f3c', margin: 0, lineHeight: 1.6 }}>{teacherFeedback.publicComment}</p>
            </div>
          )}
          {teacherFeedback.reactions && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#525c6a', marginBottom: 8 }}>Reactions</div>
              <div style={{ display: 'flex', gap: 10 }}>
                {teacherFeedback.reactions.clap    && <span style={{ fontSize: 28 }}>👏</span>}
                {teacherFeedback.reactions.heart   && <span style={{ fontSize: 28 }}>❤️</span>}
                {teacherFeedback.reactions.sparkle && <span style={{ fontSize: 28 }}>✨</span>}
              </div>
            </div>
          )}
          {teacherFeedback.suggestions?.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#525c6a', marginBottom: 8 }}>Suggestions</div>
              {teacherFeedback.suggestions.map((s: string, i: number) => (
                <div key={i} style={{ background: '#ede8ff', border: '2px solid #6a37d4', borderRadius: 8, padding: 12, fontSize: 13, color: '#252f3c', marginBottom: 8 }}>• {s}</div>
              ))}
            </div>
          )}
          {teacherFeedback.status === 'returned' && (
            <button onClick={async () => {
              if (!storyId) return;
              await StoryService.updateStory(currentUser!.uid, storyId, { status: 'published', submittedAt: Timestamp.now() });
              setTeacherFeedback({ ...teacherFeedback, status: 'published' });
            }} style={{ ...pillBtn, width: '100%', padding: 14, background: 'linear-gradient(135deg, #006947, #58e7ab)', color: '#fff', fontSize: 15, fontWeight: 700 } as any}>
              🔄 Resubmit to Class
            </button>
          )}
        </>
      )}
    </div>
    <style>{`@keyframes slideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }`}</style>
  </div>
);
 
// ─── SortableBlock ────────────────────────────────────────────────────────────
const SortableBlock: React.FC<{ id: string; children: React.ReactNode }> = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  return (
    <div ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 }}>
      <div style={{ position: 'relative' }}>
        <div {...attributes} {...listeners} style={{ position: 'absolute', left: -32, top: '50%', transform: 'translateY(-50%)', cursor: 'grab', fontSize: 18, padding: 6, background: '#eaf1ff', borderRadius: 8, color: '#a4aebe', zIndex: 10 }} title="Drag to reorder">⋮⋮</div>
        {children}
      </div>
    </div>
  );
};
 
// ─── TextBlockComponent ───────────────────────────────────────────────────────
const TextBlockComponent: React.FC<{
  block: ChapterBlock;
  chapterIndex: number;
  onUpdate: (content: string) => void;
  onDelete: () => void;
  onFocus: (editor: ReturnType<typeof useEditor>) => void;
}> = ({ block, chapterIndex, onUpdate, onDelete, onFocus }) => {
  const [isFocused, setIsFocused]   = useState(false);
  const [isEmpty, setIsEmpty]       = useState(!block.content || block.content === '<p></p>');
  const [starters, setStarters]           = useState<string[]>([]);
const [showStarterPanel, setShowStarterPanel] = useState(false);
const showStarters = showStarterPanel && starters.length > 0;
 
  const blockEditor = useEditor({
    extensions: [
      StarterKit,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Underline,
    ],
    content: block.content || '',
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      const empty = editor.isEmpty;
      setIsEmpty(empty);
      onUpdate(html);
    },
    onFocus: ({ editor }) => {
      setIsFocused(true);
      // Generate starters lazily on first focus
      
      onFocus(editor);
    },
    onBlur: () => {
      // Small delay so click on a starter registers before blur hides the panel
      setTimeout(() => setIsFocused(false), 150);
    },
    onSelectionUpdate: ({ editor }) => onFocus(editor),
  });
 
  const insertStarter = (text: string) => {
    if (!blockEditor) return;
    blockEditor.chain().focus().insertContent(text).run();
    setStarters([]);
  };
 
  return (
    <div style={{ position: 'relative', marginBottom: 16 }}>
      <div
        style={{ border: `2px solid ${isFocused ? '#ae8dff' : '#dce9fd'}`, borderRadius: 16, padding: '16px 18px', background: '#fff', boxShadow: isFocused ? '0 4px 16px rgba(106,55,212,0.08)' : '0 2px 8px rgba(106,55,212,0.04)', transition: 'border-color .2s, box-shadow .2s' }}
      >
        <button onClick={onDelete} style={{ position: 'absolute', top: 10, right: 10, background: '#fff0f3', color: '#b41340', border: '2px solid #ffc8d3', borderRadius: 8, padding: '4px 10px', cursor: 'pointer', fontSize: 12, fontWeight: 700, zIndex: 2 }}>Delete</button>
        <EditorContent editor={blockEditor} />

    {/* Spark button */}
<div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
    <button
        onMouseDown={e => {
            e.preventDefault();
            if (!starters.length) setStarters(getStarters(chapterIndex));
            setShowStarterPanel(p => !p);
        }}
        style={{ background: showStarterPanel ? C.primaryContainer : 'transparent', border: `1.5px solid ${showStarterPanel ? C.primary : C.outlineVariant}`, borderRadius: 9999, padding: '4px 12px', cursor: 'pointer', fontSize: '0.72rem', fontWeight: 700, color: showStarterPanel ? C.primary : C.onSurfaceVariant, fontFamily: "'Plus Jakarta Sans', sans-serif", transition: 'all 0.15s' }}>
        ✨ Need a spark?
    </button>
</div>

      </div>
 
      {/* Story starters panel */}
      {showStarters && (
        <div style={{
          marginTop: 8, padding: '14px 16px', background: C.surfaceLow,
          border: `2px dashed ${C.primaryLight}`, borderRadius: 16,
          animation: 'fadeSlideIn .2s ease-out',
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.onSurfaceVariant, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            ✨ Need a spark? Pick a starter:
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {starters.map((s, i) => (
              <button key={i} onMouseDown={() => insertStarter(s)} style={{
                padding: '10px 14px', background: '#fff', border: `2px solid ${C.surfaceHighest}`,
                borderRadius: 12, cursor: 'pointer', textAlign: 'left', fontSize: 14,
                color: C.onSurface, lineHeight: 1.5, fontFamily: "Georgia, serif",
                transition: 'border-color .15s, background .15s',
              }}
                onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = C.primaryLight; (e.currentTarget as HTMLButtonElement).style.background = C.primaryContainer; }}
                onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = C.surfaceHighest; (e.currentTarget as HTMLButtonElement).style.background = '#fff'; }}
              >
                "{s}"
              </button>
            ))}
          </div>
          <button onMouseDown={() => setStarters(getStarters(chapterIndex))} style={{ marginTop: 10, background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: C.onSurfaceVariant, fontWeight: 600 }}>
            🔄 Different ideas
          </button>
        </div>
      )}
    </div>
  );
};
 
// ─── ImageBlockComponent ──────────────────────────────────────────────────────
const ImageBlockComponent: React.FC<{ block: ChapterBlock & { uploading?: boolean }; onDelete: () => void }> = ({ block, onDelete }) => (
  <div style={{ position: 'relative', border: `2px solid ${(block as any).uploading ? C.tertiaryContainer : '#dce9fd'}`, borderRadius: 16, padding: '20px 24px', marginBottom: 16, background: '#fff', textAlign: 'center', boxShadow: '0 2px 8px rgba(106,55,212,0.04)', transition: 'border-color .3s' }}>
    <button onClick={onDelete} style={{ position: 'absolute', top: 10, right: 10, background: '#fff0f3', color: '#b41340', border: '2px solid #ffc8d3', borderRadius: 8, padding: '4px 10px', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>Delete</button>
    {(block as any).uploading && (
      <div style={{ position: 'absolute', top: 10, left: 14, display: 'flex', alignItems: 'center', gap: 6, background: C.tertiaryContainer, borderRadius: 9999, padding: '3px 10px', fontSize: 11, fontWeight: 700, color: C.tertiary }}>
        <div style={{ width: 8, height: 8, borderRadius: '50%', background: C.tertiary, animation: 'pulse 1s ease-in-out infinite' }} />
        Uploading…
      </div>
    )}
    {block.imageUrl && block.imageUrl !== 'placeholder-url'
      ? <img src={block.imageUrl} alt={block.caption || ''} style={{ maxWidth: '100%', height: 'auto', borderRadius: 8, opacity: (block as any).uploading ? 0.7 : 1, transition: 'opacity .3s' }} />
      : <div style={{ padding: '40px', background: '#f3f6ff', borderRadius: 8, color: '#a4aebe', fontSize: 14 }}>📸 Image loading…</div>}
    {block.caption && <div style={{ marginTop: 8, fontSize: 13, color: '#525c6a', fontStyle: 'italic' }}>{block.caption}</div>}
    <style>{`@keyframes pulse { 0%,100% { opacity:1; } 50% { opacity:0.3; } }`}</style>
  </div>
);
 
export default RegularBookEditor;
